import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  getAvatarColor,
  type AvatarSize,
} from '@heidi/design-system/avatar';

export interface WorkspaceAvatarProps {
  id: string;
  name: string;
  iconUrl: string | null;
  size?: AvatarSize;
}

// Trims before taking the character: a leading space is truthy, so
// `' Clinic'.charAt(0)` would pass the fallback guard and render a blank
// avatar. Iterates by code point rather than `charAt` so an emoji-led name
// yields the emoji instead of half a surrogate pair.
const initial = (name: string): string => [...name.trim()][0]?.toUpperCase() ?? '?';

/**
 * A workspace's identity mark: its icon when the backend has one, else the
 * initial of its name over a colour derived from its id, so the same workspace
 * looks the same wherever it is listed (sidebar switcher, login selector).
 *
 * `icon_url` is a reserved backend field that returns `null` today; the avatar
 * lights up once the backend populates it, no FE change needed.
 *
 * Extracted from the workspace top menu
 * (where it was module-private) so the login selector shares it — history:
 * `git log --follow packages/workspace/src/ui/workspace-top-menu/workspace-top-menu.tsx`.
 */
export const WorkspaceAvatar = ({ id, name, iconUrl, size }: WorkspaceAvatarProps) => (
  // Radix keeps "loaded" after image unmount; remount resets it.
  <Avatar key={iconUrl ?? ''} color={getAvatarColor(id)} size={size}>
    {/* Decorative: every consumer spells the workspace name out beside the
        avatar (or names the control itself), so a non-empty alt would double
        the accessible name once `icon_url` populates. */}
    {iconUrl && <AvatarImage src={iconUrl} alt="" className="object-cover" />}
    <AvatarFallback>{initial(name)}</AvatarFallback>
  </Avatar>
);
